import { ClientProvider } from 'hooks/webapi/useClient';
import { ClientBudgetProvider } from 'hooks/webapi/useClientBudget';
import { ClientHouseholdProvider } from 'hooks/webapi/useClientHousehold';
import { ConsentsProvider } from 'hooks/webapi/useConsents';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import GuardRoute from 'Router/GuardRoute';
import * as R from 'Router/routes';
import { Button, Layout } from 'components';
import useError from 'hooks/useError';
import useProgress from 'hooks/useProgress';
import { CalculatorProvider } from 'hooks/calculator/CalculatorProvider';
import ConstraintsProvider from 'hooks/context/Constraints/ConstraintsProvider';
import useAuth from 'utils/auth';
import { useKeycloakStore } from 'hooks/zustandStore';
import { useLegacyLoginAuth } from 'hooks/webapi';
import styles from './Registration.module.scss';
import Notifications from './Notifications/Notifications';

export default function Registration() {
  const location = useLocation();
  const {
    disabled = false,
    section,
    step,
    sectionList,
    showFooter,
    showProgress,
    withCalculator,
    showSidebar,
    nextStepsDisabled,
    layoutStyles,
  } = useProgress();
  const { keycloakLogout } = useAuth();
  const { t } = useTranslation('common');

  const isLegacyLogin = useKeycloakStore((state) => state.isLegacyLogin);
  const isLegacyLoginLoading = useKeycloakStore(
    (state) => state.isLegacyLoginLoading,
  );

  const { logout: LEGACY_LOGIN_logout } = useLegacyLoginAuth();

  const handleLegacyLoginLogout = async () => {
    await LEGACY_LOGIN_logout();
    window.location.assign(R.INTRO);
  };

  const prevStepRef = useRef(-1);
  const { removeError } = useError();

  useEffect(() => {
    window.scrollTo(0, 0);
    removeError();
  }, [location.pathname, removeError]);

  useEffect(() => {
    prevStepRef.current = step;
  });

  const prevStep = prevStepRef.current;

  const handleKeycloakLogout = useCallback(async () => {
    const callbackUrl = new URL(
      `${window.location.protocol}//${window.location.host}${R.INTRO}`,
    );

    keycloakLogout(callbackUrl.toString());
  }, []);

  const isAuthModeResolving = isLegacyLoginLoading || isLegacyLogin === null;

  const handleLogout = async () => {
    if (isAuthModeResolving) return;

    if (isLegacyLogin) {
      await handleLegacyLoginLogout();
      return;
    }

    await handleKeycloakLogout();
  };

  const renderNavigationItems = useCallback(() => {
    if (!sessionStorage.getItem('token') || location.pathname === R.INTRO) {
      return <div />;
    }
    return (
      <Button
        className={styles['logout-container']}
        buttonClassName={styles.logout}
        outline
        size="s"
        onClick={handleLogout}
        pending={isAuthModeResolving}
      >
        {t('logout')}
      </Button>
    );
  }, [handleLogout, location.pathname, t, isAuthModeResolving]);

  return (
    <ConstraintsProvider>
      <ClientProvider>
        <ClientHouseholdProvider>
          <ClientBudgetProvider>
            <CalculatorProvider>
              <ConsentsProvider>
                <Layout
                  withCalculator={withCalculator}
                  steps={sectionList}
                  progressDisabled={disabled}
                  currentStep={section}
                  showFooter={showFooter}
                  showProgress={showProgress}
                  transitionDirection={prevStep > step ? 'top' : 'bottom'}
                  url={window.location.href}
                  renderNavigationItems={renderNavigationItems}
                  showSidebar={showSidebar}
                  nextStepsDisabled={nextStepsDisabled}
                  layoutStyles={layoutStyles}
                >
                  <Notifications />
                  <GuardRoute>
                    <Outlet />
                  </GuardRoute>
                </Layout>
              </ConsentsProvider>
            </CalculatorProvider>
          </ClientBudgetProvider>
        </ClientHouseholdProvider>
      </ClientProvider>
    </ConstraintsProvider>
  );
}
